import { useMemo } from 'react';

import { snapToNiceValue } from '~utils';
import { useSvgContext } from '../components/SvgContext';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type UseGridSpacingProps = {
  /**
   * Desired number of grid lines visible on screen at any zoom level.
   * @default 20
   */
  targetLines?: number;
};

type UseGridSpacingReturn = {
  /**
   * Grid spacing in SVG coordinate units (local coords ≈ meters).
   * Snapped to a "nice" value (1, 2, 5 × 10^n) for readability.
   * Returns `0` if bounds are not available.
   */
  spacing: number;
};

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const DEFAULT_TARGET_LINES = 20;

/* -------------------------------------------------------------------------- */
/*                                    Hook                                    */
/* -------------------------------------------------------------------------- */

/**
 * Compute adaptive grid spacing based on the current zoom level.
 *
 * The spacing adapts so the grid always shows approximately `targetLines` lines
 * on screen. As the user zooms in, the visible extent shrinks and spacing decreases
 * (more detail). As they zoom out, spacing increases (less clutter).
 *
 * Since the canvas coordinate system uses local coordinates where 1 unit ≈ 1 meter,
 * the returned `spacing` represents a real-world distance in meters.
 *
 * @example
 * ```tsx
 * const { spacing } = useGridSpacing({ targetLines: 20 });
 * // spacing = 50 at k=1, 20 at k=2, 10 at k=4, etc.
 * ```
 */
const useGridSpacing = ({ targetLines = DEFAULT_TARGET_LINES }: UseGridSpacingProps = {}): UseGridSpacingReturn => {
  const { bounds, transform } = useSvgContext();

  const spacing = useMemo(() => {
    if (!bounds) return 0;

    const safeTargetLines = Math.max(1, targetLines);
    const extent = Math.max(bounds.width, bounds.height);
    const visibleExtent = extent / transform.k;

    return snapToNiceValue(visibleExtent / safeTargetLines);
  }, [bounds, transform.k, targetLines]);

  return { spacing };
};

export type { UseGridSpacingProps, UseGridSpacingReturn };
export { useGridSpacing };
